/**
 * 续费倒计时挽留 Modal
 * Pro 档到期前 3 天 / 1 天 弹窗挽留
 *
 * 优惠:
 *   - 3 天前:Pro 月付 9 折 ¥152 / 年付 9 折 ¥629
 *   - 1 天前:Pro 月付 8 折 ¥135 / 年付 85 折 ¥594
 *
 * 跳过按钮:7 天内不再弹(本地存储)
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Clock, Gift, Sparkles, ChevronRight, Heart
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';

const SKIP_KEY = 'fucai3d_renewal_skip';
const SKIP_DAYS = 7;  // 跳过后 7 天内不再弹

// 试用档位
const TIER_DISCOUNTS = {
  'Pro 月付': { price: 169, color: 'cyan' },
  'Pro 年付': { price: 699, color: 'purple' },
};

function getDiscount(daysLeft) {
  if (daysLeft <= 1) return 0.8;  // 1 天:8 折
  if (daysLeft <= 3) return 0.9;  // 3 天:9 折
  return 1.0;
}

export function RenewalNudgeModal({ onClose, onUpgrade }) {
  const { user, setUser } = useUser();
  const [daysLeft, setDaysLeft] = useState(0);
  const [skipping, setSkipping] = useState(false);

  useEffect(() => {
    // 计算剩余天数
    if (user?.tier_expires_at) {
      const ms = user.tier_expires_at - Date.now();
      const days = Math.ceil(ms / 86400000);
      setDaysLeft(Math.max(0, days));
    }
  }, [user]);

  if (daysLeft > 3 || daysLeft < 0) return null;

  const discount = getDiscount(daysLeft);
  const isUrgent = daysLeft <= 1;
  const monthlyDiscount = Math.round(169 * discount);
  const yearlyDiscount = Math.round(699 * discount);

  const skip7Days = () => {
    const skipUntil = Date.now() + SKIP_DAYS * 86400000;
    localStorage.setItem(SKIP_KEY, String(skipUntil));
    setSkipping(true);
    setTimeout(() => onClose(), 200);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          className="w-full sm:max-w-md glass-strong rounded-2xl p-5 relative max-h-[90vh] overflow-y-auto"
        >
          <button onClick={onClose} className="absolute top-3 right-3 text-ink-3 hover:text-ink-0">
            <X size={20} />
          </button>

          {/* 顶部图标 */}
          <div className="text-center mb-3">
            <motion.div
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={clsx(
                'inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-2',
                isUrgent ? 'bg-gradient-to-br from-accent-danger to-brand-rose' : 'bg-gradient-to-br from-brand-gold to-brand-purple',
              )}
            >
              <Clock size={32} className="text-white" />
            </motion.div>
            <h2 className="text-xl font-bold">
              {isUrgent ? '⏰ 仅剩 1 天到期!' : '📅 还剩 3 天到期'}
            </h2>
            <p className="text-sm text-ink-2 mt-1">
              续费享 <span className={clsx('font-bold', isUrgent ? 'text-accent-danger' : 'text-brand-gold')}>
                {Math.round(discount * 10)} 折
              </span> 优惠,老用户专享
            </p>
          </div>

          {/* 优惠价卡片 */}
          <div className="space-y-2 mb-4">
            <DiscountCard
              tier="Pro 月付"
              original={169}
              discount={monthlyDiscount}
              color="cyan"
              onClick={() => onUpgrade('Pro 月付', monthlyDiscount)}
            />
            <DiscountCard
              tier="Pro 年付"
              original={699}
              discount={yearlyDiscount}
              color="purple"
              saveBadge={`省 ¥${699 - yearlyDiscount}`}
              onClick={() => onUpgrade('Pro 年付', yearlyDiscount)}
            />
          </div>

          {/* 续费价值提示 */}
          <div className="p-3 rounded-xl bg-bg-base/50 border border-white/5 mb-3">
            <div className="text-[10px] text-ink-3 mb-1.5 flex items-center gap-1">
              <Heart size={10} className="text-brand-rose" /> 这 38 天,你用了
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-base font-bold text-brand-cyan">156</div>
                <div className="text-[9px] text-ink-3">收藏底池</div>
              </div>
              <div>
                <div className="text-base font-bold text-brand-purple">23</div>
                <div className="text-[9px] text-ink-3">命中注数</div>
              </div>
              <div>
                <div className="text-base font-bold text-brand-gold">38</div>
                <div className="text-[9px] text-ink-3">使用天数</div>
              </div>
            </div>
          </div>

          {/* 跳过按钮 */}
          <button
            onClick={skip7Days}
            className="w-full text-[10px] text-ink-3 hover:text-ink-2 py-2"
          >
            7 天内不再提醒 →
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function DiscountCard({ tier, original, discount, color, saveBadge, onClick }) {
  const colorMap = {
    cyan: { bg: 'bg-brand-cyan/10', border: 'border-brand-cyan/30', text: 'text-brand-cyan' },
    purple: { bg: 'bg-brand-purple/10', border: 'border-brand-purple/30', text: 'text-brand-purple' },
  };
  const c = colorMap[color];
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full p-3 rounded-xl border-2 flex items-center gap-3 transition hover:scale-[1.02]',
        c.bg, c.border,
      )}
    >
      <div className="flex-1 text-left">
        <div className="flex items-center gap-2">
          <span className={clsx('text-sm font-bold', c.text)}>{tier}</span>
          {saveBadge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold font-bold">{saveBadge}</span>}
        </div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-xs text-ink-3 line-through">¥{original}</span>
          <span className={clsx('text-xl font-bold font-mono', c.text)}>¥{discount}</span>
        </div>
      </div>
      <ChevronRight size={18} className={c.text} />
    </button>
  );
}

// Hook:检查是否应该弹挽留 modal
export function useRenewalNudge() {
  const { user } = useUser();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user || !user.tier_expires_at) return;
    // 试用 / MAX 永久(无过期)不弹
    if (user.tier_expires_at === null || user.tier_expires_at === undefined) return;
    // 检查用户是否跳过
    const skipUntil = +localStorage.getItem(SKIP_KEY) || 0;
    if (skipUntil > Date.now()) return;
    // 计算剩余天数
    const ms = user.tier_expires_at - Date.now();
    const days = Math.ceil(ms / 86400000);
    if (days >= 0 && days <= 3) {
      // 延后 2 秒弹,避免跟其他 modal 冲突
      const t = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(t);
    }
  }, [user]);

  return { show, close: () => setShow(false) };
}
